/**
 * Ficha de un albarán, con la firma dibujada.
 *
 * Antes sólo existía el listado: desde la oficina se sabía QUE un albarán
 * estaba firmado, pero no se podía ver QUÉ se firmó ni el trazo. Un albarán
 * existe precisamente para el día que un cliente discute una entrega, y para
 * ese día el listado no sirve de nada.
 *
 * La firma se enseña con lo que se guardó: el trazo, sin datos biométricos
 * (RGPD §3.3). Junto a ella va la cadena de custodia —quién la recogió, con
 * qué terminal y sobre qué huella—, que es lo que la hace defendible.
 */

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useSession } from '../../session/session-context.js';
import { ApiError } from '../../session/api-error.js';
import { DatumGroup, Datum, DetailShell, type Tab } from './detail-shell.js';
import { day, dateTime } from '../../table/format.js';
import { PdfButton } from '../pdf-button.js';

interface Linea {
  ordinal: number;
  description: string;
  quantity: string;
}

interface Firma {
  id: string;
  signerName: string;
  signerPosition: string | null;
  signerRole: string;
  signedAt: string | null;
  signedDocumentHash: string;
  deviceId: string | null;
  capturedBy: string | null;
  strokeSvg: string | null;
  unavailableReason: string | null;
}

interface Detalle {
  note: {
    id: string;
    number: string;
    status: string;
    delivered_on: string;
    customer_name: string;
    site_name: string | null;
    technician_name: string | null;
    signed_by_name: string | null;
  };
  lines: Linea[];
  signature: Firma | null;
}

const ROLES: Record<string, string> = {
  PROPERTY: 'La propiedad',
  MAINTAINER: 'El mantenedor',
  TECHNICIAN: 'El técnico',
  INSPECTOR: 'El inspector',
  OTHER: 'Otro',
};

export function DeliveryNoteDetailScreen(): React.ReactNode {
  const { api } = useSession();
  const { id } = useParams<{ id: string }>();

  const [detalle, setDetalle] = useState<Detalle | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    let cancelado = false;

    api
      .get<Detalle>(`/delivery-notes/${id}`)
      .then((datos) => {
        if (!cancelado) setDetalle(datos);
      })
      .catch((caught: unknown) => {
        if (!cancelado) {
          setError(caught instanceof ApiError ? caught.message : 'No se ha podido cargar el albarán.');
        }
      })
      .finally(() => {
        if (!cancelado) setCargando(false);
      });

    return () => {
      cancelado = true;
    };
  }, [api, id]);

  const firma = detalle?.signature ?? null;

  const tabs: Tab[] = [
    {
      id: 'resumen',
      label: 'Entrega',
      render: () => (
        <>
          <DatumGroup title="Datos">
            <Datum label="Cliente">{detalle?.note.customer_name ?? '—'}</Datum>
            <Datum label="Centro">{detalle?.note.site_name ?? '—'}</Datum>
            <Datum label="Técnico">{detalle?.note.technician_name ?? 'Sin asignar'}</Datum>
            <Datum label="Entregado">{day(detalle?.note.delivered_on ?? null)}</Datum>
          </DatumGroup>

          <DatumGroup title="Conceptos">
            {(detalle?.lines ?? []).map((l) => (
              <Datum key={l.ordinal} label={`${l.quantity} ×`}>
                {l.description}
              </Datum>
            ))}
            {(detalle?.lines ?? []).length === 0 && (
              <Datum label="—">Sin conceptos</Datum>
            )}
          </DatumGroup>
        </>
      ),
    },
    {
      id: 'firma',
      label: 'Firma',
      render: () =>
        firma === null ? (
          <p className="screen__note">
            Este albarán todavía no lo ha firmado el cliente. Sin firma no vale como prueba
            de entrega.
          </p>
        ) : (
          <>
            <DatumGroup title="Quién firmó">
              <Datum label="Nombre">{firma.signerName}</Datum>
              <Datum label="Cargo">{firma.signerPosition ?? '—'}</Datum>
              <Datum label="En calidad de">{ROLES[firma.signerRole] ?? firma.signerRole}</Datum>
              <Datum label="Fecha">{dateTime(firma.signedAt)}</Datum>
            </DatumGroup>

            <DatumGroup title="Trazo">
              {firma.strokeSvg !== null ? (
                <div
                  className="signature"
                  aria-label={`Firma de ${firma.signerName}`}
                  // El trazo es un SVG que compuso nuestra propia app a partir
                  // de los puntos del dedo: sólo caminos, sin script.
                  dangerouslySetInnerHTML={{ __html: firma.strokeSvg }}
                />
              ) : (
                <p className="screen__error">
                  {firma.unavailableReason ?? 'El trazo no está disponible'}
                </p>
              )}
            </DatumGroup>

            <DatumGroup title="Cadena de custodia">
              <Datum label="Recogida por">{firma.capturedBy ?? '—'}</Datum>
              <Datum label="Terminal">{firma.deviceId ?? '—'}</Datum>
              <Datum label="Huella de lo firmado">
                <code>{firma.signedDocumentHash}</code>
              </Datum>
            </DatumGroup>
          </>
        ),
    },
  ];

  return (
    <DetailShell
      title={detalle?.note.number ?? 'Albarán'}
      subtitle={
        <Link to="/albaranes">
          ← Albaranes
        </Link>
      }
      badges={detalle === null ? null : <span className="pill">{detalle.note.status}</span>}
      actions={
        firma === null ? null : (
          <PdfButton
            kind="DELIVERY_NOTE"
            entityId={detalle!.note.id}
            sourceHash={firma.signedDocumentHash}
            label="Imprimir albarán"
          />
        )
      }
      tabs={tabs}
      loading={cargando}
      error={error}
    />
  );
}
